// Intended sedation level(s) and Host Compliance Check for the Key Points Review (S-14, S-15).
import type { HostComplianceCheck, SedationLevel } from '../hooks/repos/sedationDetailRepo';

export const STANDARD_LEVELS: SedationLevel[] = ['Minimal', 'Moderate', 'Deep', 'General'];
export const PROCEDURAL: SedationLevel = 'Procedural';
export const DEFAULT_LEVELS: SedationLevel[] = ['Minimal', 'Moderate'];

/**
 * Levels for a stored technique. Records made before multi-select only have IntendedLevel:
 * once key points were recorded that is what the sedationist chose; never-reviewed records
 * (e.g. created by a HUB booking) get today's default.
 */
export const levelsFromStored = (
  technique: { IntendedLevel?: SedationLevel | null; IntendedLevels?: SedationLevel[] | null } | undefined,
  recordedAt: string | null | undefined
): SedationLevel[] => {
  if (technique?.IntendedLevels?.length) return technique.IntendedLevels;
  if (recordedAt && technique?.IntendedLevel) return [technique.IntendedLevel];
  return DEFAULT_LEVELS;
};

/** Single level for readers that predate IntendedLevels: Procedural, else the highest selected. */
export const primaryLevel = (levels: SedationLevel[]): SedationLevel => {
  if (levels.includes(PROCEDURAL)) return PROCEDURAL;
  const highest = [...STANDARD_LEVELS].reverse().find(l => levels.includes(l));
  return highest ?? 'Moderate';
};

export interface LevelToggleResult {
  levels: SedationLevel[];
  /** Procedural sedation switches "Not asleep" and "Aim: anxiolysis" off; leaving it switches them back on. */
  responsivenessToggles?: boolean;
}

/**
 * Procedural is exclusive: choosing it clears the others. Choosing a standard level leaves
 * Procedural. The last remaining level can't be deselected.
 */
export const toggleLevel = (current: SedationLevel[], level: SedationLevel): LevelToggleResult => {
  if (level === PROCEDURAL) {
    return current.includes(PROCEDURAL) ? { levels: current } : { levels: [PROCEDURAL], responsivenessToggles: false };
  }
  if (current.includes(PROCEDURAL)) return { levels: [level], responsivenessToggles: true };
  const next = current.includes(level) ? current.filter(l => l !== level) : [...current, level];
  if (next.length === 0) return { levels: current };
  return { levels: STANDARD_LEVELS.filter(l => next.includes(l)) };
};

export const HOST_COMPLIANCE_ITEMS: Array<{ key: keyof HostComplianceCheck; label: string }> = [
  { key: 'OxygenAndAirwayEquipmentChecked', label: 'Oxygen & airway equipment checked' },
  { key: 'ResuscitationEquipmentChecked', label: 'Resuscitation equipment (AED) checked' },
  { key: 'EmergencyDrugsInDate', label: 'Emergency drugs in-date' },
  { key: 'EscalationConfirmed', label: 'Escalation confirmed (means of summoning help)' },
  { key: 'SurgeonAndNurseIlsCertified', label: 'Surgeon and Nurse ILS certified' },
];

export const DEFAULT_HOST_COMPLIANCE: HostComplianceCheck = {
  OxygenAndAirwayEquipmentChecked: true,
  ResuscitationEquipmentChecked: true,
  EmergencyDrugsInDate: true,
  EscalationConfirmed: true,
  SurgeonAndNurseIlsCertified: true,
};
